<template>
    <v-row
        @click="onClick()"
        :data-cy="dataCyId + '_Click'"
        no-gutters
        class="pa-3 fill-height rounded-xl bg-primary-foreground"
        :class="[borderClass, cursorPointerClass]"
    >
        <v-col
            cols="2"
            class="d-flex align-center justify-center"
            :class="borderRightClass"
        >
            <v-icon color="red"> $fa-exclamation-triangle </v-icon>
        </v-col>
        <v-col cols="10" class="d-flex align-center pl-2" :data-cy="dataCyId + '_Message'">
            {{ message }}
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { Component, toNative, Prop, Vue } from "vue-facing-decorator";
import { RouteLocationRaw } from "vue-router";

import { AuditCheckDocumentName } from "@/views/document/enum/AuditCheckDocumentName.enum";

/**
 * Component for displaying an audit check card.
 * Currently, handles only audit check documents, but can be updated to handle other types of audit checks.
 */
@Component
export class AuditCheckCard extends Vue {
    /**
     * Count of audit check items. Mandatory prop.
     */
    @Prop({ required: true }) readonly auditCheckItemsCount!: number;

    /**
     * Name of the audit check.
     */
    @Prop() readonly auditCheckName: AuditCheckDocumentName;

    /**
     * Indicates if the card should redirect to the documents page.
     */
    @Prop({ default: false }) readonly shouldRedirectOnClick: boolean;

    /**
     * Localized message key for the card message.
     * Used when no audit check name is provided.
     */
    @Prop({ default: "CheckDocumentAnomaly" }) readonly messageRessourceKey: string;

    /**
     * Indicates if the card has a dark border.
     */
    @Prop({ default: false }) readonly hasDarkBorder: boolean;

    /**
     * Returns the AuditCheckDocumentName enum type for the template.
     */
    get AuditCheckDocumentName(): typeof AuditCheckDocumentName {
        return AuditCheckDocumentName;
    }

    /**
     * Returns the localized message for the card.
     */
    get message(): string {
        let message = "";
        if (this.auditCheckName) {
            message = this.$t(`audit.${this.auditCheckName}.countMessage`, {
                count: this.auditCheckItemsCount,
            });
        } else {
            message = this.$t(`audit.${this.messageRessourceKey}.countMessage`, {
                count: this.auditCheckItemsCount,
            });
        }

        return message;
    }

    /**
     * Returns the data-cy id for the card.
     */
    get dataCyId(): string {
        return `AuditCheckCard_${this.auditCheckName ?? this.messageRessourceKey}`;
    }

    /**
     * Returns a css border class.
     */
    get borderClass(): string {
        return this.hasDarkBorder ? "dark-border" : "basic-border";
    }

    /**
     * Returns a css border-right class.
     */
    get borderRightClass(): string {
        return this.hasDarkBorder ? "dark-right-border" : "basic-right-border";
    }

    /**
     * Returns a css cursor-pointer class.
     */
    get cursorPointerClass(): string {
        return this.shouldRedirectOnClick ? "cursor-pointer" : "";
    }

    /**
     * Handles the click event on the card.
     */
    onClick(): void {
        if (this.shouldRedirectOnClick) {
            this.navigateToDocumentsWithCheckFilter(this.auditCheckName);
        }
    }

    /**
     * Emits a clicked event and navigates to the documents page with query parameters to reset the filters and to set a filter on the audit check.
     * @param auditCheckName Name of the wanted AuditCheck as filter for the documents page
     */
    navigateToDocumentsWithCheckFilter(auditCheckName: AuditCheckDocumentName): void {
        this.$emit("auditCheckCardClicked");

        const location: RouteLocationRaw = {
            path: "/documents",
            query: { auditCheck: auditCheckName, resetFilter: "true" },
        };

        this.$router.push(location);
    }
}

export default toNative(AuditCheckCard);
</script>
<style lang="scss" scoped>
$dark-border-style: 1px darkGrey solid;
$basic-border-style: 1px #ededed solid;

.dark-border {
    border: $dark-border-style;
}
.dark-right-border {
    border-right: $dark-border-style;
}
.basic-border {
    border: none;
}
.basic-right-border {
    border-right: $basic-border-style;
}
</style>